import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams();
  const article = newsList.find((item) => String(item.id) === id);

  usePageMeta({
    title: article ? `${article.title} - 电影天堂资讯` : "资讯详情 - 电影天堂",
    description: article ? article.summary : "电影天堂影视资讯详情。",
    keywords: "电影天堂,电影资讯,影视动态,票房",
  });

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-newspaper-line text-6xl text-foreground-500" />
          <h1 className="mt-5 text-2xl font-bold text-foreground-50">未找到该资讯</h1>
          <Link
            to="/news"
            className="mt-6 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-medium text-background-950 transition hover:bg-accent-400"
          >
            返回资讯列表
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/news" className="hover:text-accent-400">电影资讯</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="truncate text-foreground-100">{article.title}</span>
          </nav>

          <article>
            <h1 className="text-2xl font-bold leading-snug text-foreground-50 md:text-3xl">
              {article.title}
            </h1>
            <p className="mt-3 flex items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-price-tag-3-line" />
                影视资讯
              </span>
            </p>

            <div className="mt-6 overflow-hidden rounded-lg ring-1 ring-background-200/70">
              <img
                src={article.cover}
                alt={`${article.title}配图`}
                title={`${article.title} - 电影天堂资讯`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-6 space-y-4">
              {article.content.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-8 text-foreground-300 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>
          </article>

          <section className="mt-12">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-50">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              相关资讯
            </h2>
            <div className="mt-5 space-y-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-center gap-4 rounded-lg bg-background-100 p-4 ring-1 ring-background-200/70 transition hover:ring-accent-500/60"
                >
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title}配图`}
                      title={`${item.title} - 电影天堂资讯`}
                      className="h-full w-full object-top transition duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-sm font-medium text-foreground-100 transition group-hover:text-accent-400">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{item.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <div className="mt-10 flex justify-center">
            <Link
              to="/news"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-100 transition hover:border-accent-500/70 hover:text-accent-400"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}